import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { HttpHeaders } from '@angular/common/http';

import { Observable, from, BehaviorSubject, throwError } from 'rxjs';
import { tap, map, filter, startWith, switchMap, catchError } from 'rxjs/operators';

import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';

import {
   EnvironmentService,
   ErrorMessageService,
   MaintenanceService,
   ResetService,
} from '@library/core/services';
import { ThemeService } from '@library/common/branding';
import { UserService } from '@library/modules/user';

import { Auth, CognitoUser } from '@aws-amplify/auth';

export enum AuthStates {
   SignedOut = 'signedout',
   SignedIn = 'signedin',
   VerifyEmail = 'verifyemail',
   ForgotPassword = 'forgotpassword',
   ResetPassword = 'resetpassword',
   NewPassword = 'newpassword',
}

@Injectable({
   providedIn: 'root',
})
export class AuthService {
   lastUrl = '/';
   locked = false;
   working = false;

   authState$: BehaviorSubject<string> = new BehaviorSubject( AuthStates.SignedOut );

   CognitoUser: CognitoUser;
   activeEmail: string;

   authSnackbar: MatSnackBarRef<any>;

   _loggedIn = false;
   get isLoggedIn$(): Observable<boolean> {
      return this.session.pipe(
         map( session => session?.isValid() || false ),
         map( _ => this.e.environment?.mock ? true : _ ),
         tap( loggedIn => this._loggedIn = loggedIn ),
      );
   }

   get session() {
      return from( Auth.currentSession().catch( _ => null ));
   }

   get currentUser(): Observable<CognitoUser> {
      return from( Auth.currentUserPoolUser({ bypassCache: true }).catch( _ => null ));
   }

   constructor(
      private router: Router,
      private theme: ThemeService,
      private e: EnvironmentService,
      private error: ErrorMessageService,
      private user: UserService,
      private reset: ResetService,
      private snackbar: MatSnackBar,
      private maintenance: MaintenanceService,
   ) {
      // listen for branding changes
      this.theme.themeChange$.pipe(
         startWith( this.theme.currentTheme ),
      ).subscribe( theme =>
         Auth.configure({
            authenticationFlowType: 'USER_PASSWORD_AUTH',
            clientMetadata: {
               portal: document.title,
               brand: theme.class,
               url: document.location.origin,
            }
         })
      );

      // on load match the session state
      this.isLoggedIn$.pipe(
         filter( signedin => !!signedin ),
         switchMap( _ => this.currentUser ),
      ).subscribe( cognitoUser => this.handleSignedInState( cognitoUser ));

      // set last url
      this.router.events.pipe(
         filter( event => event instanceof NavigationEnd ),
         filter(( event: NavigationEnd ) => ![ '/login', '/logout', '/lock' ].includes( event.url )),
      ).subscribe( _ => this.lastUrl = this.router.url );
   }

   handleSignedInState( user: CognitoUser = null ) {
      if ( !user ) { return; }

      this.CognitoUser = user;
      if (( user as any )?.challengeName === 'NEW_PASSWORD_REQUIRED' ) {
         // create a new password
         this.authState$.next( AuthStates.NewPassword );
         this.activeEmail = ( user as any )?.challengeParam?.userAttributes?.email;
         this.working = false;
      } else if ( !(user as any).attributes?.email_verified ) {
         this.authState$.next( AuthStates.VerifyEmail );
         this.sendVerificationEmail();
         this.working = false;
      } else {
         this.authState$.next( AuthStates.SignedIn );
         this.user.user.pipe(
            catchError( error => {
               if ( error.error.messages?.length ) {
                  console.log( 'sign in error', error.error.messages );
                  localStorage.setItem( '__initialLogin', 'true' );

                  this.authSnackbar = this.snackbar.open( error.error.messages[0].message, 'Close' );
                  this.authSnackbar.onAction().subscribe( _ => this.authSnackbar.dismiss() );

                  return throwError( error.error.messages[0].message );
               }

               this.logout();

               setTimeout(() => this.error.push( 'This portal is not currently working, please contact your administrator' ), 100 );

               return throwError( 'Cannot retrieve user' );
            }),
         ).subscribe( _ => {
            if ( [ '/login', '/logout', '/lock' ].includes( this.router.url )) {
               this.router.navigate([ '/' ]);
            }
         });
      }
   }

   setAuthHeaders( headers: HttpHeaders ): Observable<HttpHeaders> {
      return this.session.pipe(
         map( session => {
            if ( !session || !session.isValid() ) { return headers; }

            if ( headers.get('Authorization') ) { return headers; }

            let authHeaders = `Bearer ${session.getAccessToken().jwtToken}`;

            if ( JSON.parse( localStorage.getItem( '__initialLogin' ) ) ) {
               authHeaders += '.' + session.getIdToken().jwtToken;
               localStorage.setItem( '__initialLogin', 'false' );

               localStorage.setItem('initKey', authHeaders);
            }

            headers = headers.set( 'Authorization', authHeaders );
            // console.log( 'set headers', headers );

            return headers;
         }),
      );
   }

   login( username: string, password: string ): Observable<CognitoUser> {
      this.working = true;

      return from( Auth.signIn( username, password )).pipe(
         tap( _ => localStorage.setItem( '__initialLogin', 'true' ) ),
         // tap( response => console.log( 'response', response )),
         tap( cognitoUser => this.handleSignedInState( cognitoUser )),
         tap( _ => this.maintenance.check() ),
         // dismiss snackbar when login success
         tap( _ => this.authSnackbar ? this.authSnackbar.dismiss() : null ),
         catchError( error => {
            if ( error.message ) {
               // auto dismiss snackbar within 15secs when there's an error.
               this.authSnackbar = this.snackbar.open( error.message, 'Close', { duration: 15000 } );
               this.authSnackbar.onAction().subscribe( _ => this.authSnackbar.dismiss() );
               this.working = false;
            }

            throw error;
         }),
      );
   }

   async logout() {
      this.working = true;

      await Auth.signOut().then( _ => {
         this.authState$.next( AuthStates.SignedOut );
         localStorage.clear();
         this.user.destroy();
         this.reset.reset();
         this.router.navigate([ 'login' ]);
         this.maintenance.check();
         this.working = false;
      });
   }

   async lock( url: string = this.router.url ) {
      this.working = true;
      this.lastUrl = url === '/lock' ? '/' : url;
      await Auth.signOut().then( _ => {
         this.authState$.next( AuthStates.SignedOut );
         this.router.navigate([ 'login' ]);
         this.maintenance.check();
         this.working = false;
      });
   }

   sendVerificationEmail() {
      this.working = true;
      this.activeEmail = ( this.CognitoUser as any ).attributes.email;

      Auth.verifyUserAttribute( this.CognitoUser, 'email' ).then( _ => this.working = false );
   }

   verifyEmail( code: string ) {
      this.working = true;

      return from( Auth.verifyUserAttributeSubmit( this.CognitoUser, 'email', code )).pipe(
         switchMap( _ => Auth.currentUserPoolUser({ bypassCache: true })),
         tap( user => this.handleSignedInState( user )),
         tap( _ => this.activeEmail = null ),
         tap( _ => this.working = false ),
      );
   }

   sendForgotPasswordEmail( email: string ) {
      this.working = true;
      this.activeEmail = email;

      Auth.forgotPassword( email ).then( _ => {
         this.working = false;
         this.authState$.next( AuthStates.ResetPassword );
      }, error => {
         this.authSnackbar = this.snackbar.open( error.message ?? 'Unknown Auth Error please contact your administrator', 'Close' );
         this.authSnackbar.onAction().subscribe( _ => this.authSnackbar.dismiss() );
         this.working = false;
      });
   }

   resetPassword( code: string, password: string ) {
      this.working = true;

      return Auth.forgotPasswordSubmit( this.activeEmail, code.trim(), password ).then( _ => {
         this.login( this.activeEmail, password ).subscribe();
         this.activeEmail = null;
      }, error => {
         this.authSnackbar = this.snackbar.open( error.message ?? 'Unknown Auth Error please contact your administrator', 'Close' );
         this.authSnackbar.onAction().subscribe( _ => this.authSnackbar.dismiss() );
         this.working = false;
      });
   }

   newPassword( password: string ) {
      this.working = true;

      const currentUser = { ...this.CognitoUser };
      console.log( 'currentUser', currentUser, this.CognitoUser );

      return Auth.completeNewPassword( this.CognitoUser, password ).then( _ => {
         this.login( this.activeEmail, password ).subscribe();
         this.activeEmail = null;
      }, error => {
         this.authSnackbar = this.snackbar.open( error.message ?? 'Unknown Auth Error please contact your administrator', 'Close' );
         this.authSnackbar.onAction().subscribe( _ => this.authSnackbar.dismiss() );
         this.working = false;
      })
   }

   
}
